/**
 * StaffAdmin — 직원 초대·승인·활동 로그 (관리자 이상, 2026-07-24 초대링크 전환).
 * 백엔드: /api/staff(목록)·/invite(발급)·/invites(목록)·/approve·/block·/activity.
 * 가입은 초대 링크로만 — 여기서 링크를 만들어 직원에게 보낸다(Slack식).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { API_BASE_URL, apiFetch } from "./utils";
import { friendlyErrorMessage } from "./ds/friendlyError";
import { useRole, useAuth } from "./auth/AuthContext";
import { roleAtLeast, ACCESS_FEATURES, DEFAULT_ACCESS, mergeAccess, ROLE_LABEL, ROLE_ORDER, deniedFor, DEFAULT_DENY } from "./auth/access";
import { groupActivities } from "./ds/activityLabel";

// 역할 라벨·순서는 auth/access 가 단일 SoT — 여기서 복제하면 이름 변경 때 어긋난다
// (실사례: '임원'→'진현' 개명 시 3곳이 따로 놀던 것, 2026-07-26 정리).
const PERM_ROLE_OPTS = ROLE_ORDER.map((r) => [r, ROLE_LABEL[r]]);
const PERM_COLS = PERM_ROLE_OPTS;
const _ORD = ROLE_ORDER;

// 요약표 행 — 이미지 레이아웃. features=[] 는 게이트 없는 전원 공통.
const SUMMARY_ROWS = [
  { label: "견적 만들기·목록·프랜차이즈·클립보드·휴지통", features: ["estimate"] },
  // QCC·기타문서는 현장팀 개방(사장 지시 2026-07-28) — 견적 행에서 분리.
  { label: "QCC", features: ["qcc"] },
  { label: "기타문서", features: ["docs"] },
  { label: "설정 › 테마", features: [] },
  { label: "잔금내역서", features: ["balance"] },
  { label: "단가 DB·브랜드", features: ["recipedb", "brands"] },
  { label: "계약서·전자계약", features: ["contract", "esign"] },
  { label: "설정 › 미등록 단가 요청", features: ["unmatched"] },
  { label: "AS유지보수 관리", features: ["as_ops"] },
  { label: "SMBM", features: ["smbm"] },
  { label: "SMBM lite(보조 사업자)", features: ["smbm_lite"] },
  { label: "영업 파이프라인", features: ["pipeline"] },
  { label: "정산", features: ["settlement"] },
  { label: "설정 › 사업자정보·전자계약서명", features: ["bizinfo", "esignseals"] },
  { label: "직원 관리", features: ["staffadmin"] },
];

/** 그룹 행의 최소역할 = 가장 제한적인(높은) 것 — 그 역할이 그룹 전 기능을 볼 수 있어야 ✅.
 *
 *  초기값은 최하위 역할(field). 예전엔 "staff" 로 시작해서 features=[] (게이트 없는
 *  전원 공통 행, 예: '설정 › 테마')이 현장팀 열에만 ✗ 로 그려지는 표시 버그가 있었다
 *  (실제 /settings 라우트는 가드 없음 — 2026-07-28 수정).
 *  결측 기본값도 "staff" 고정이 아니라 DEFAULT_ACCESS 를 따른다(qcc·docs 는 def=field). */
export function rowMin(perm, features) {
  let m = _ORD[0];
  for (const f of features) {
    const r = permMin(perm, f);
    if (_ORD.indexOf(r) > _ORD.indexOf(m)) m = r;
  }
  return m;
}

/** 기능 하나의 최소역할 — 서버 병합 config 우선, 없으면 프론트 기본값. */
function permMin(perm, f) {
  return (perm && perm[f]) || DEFAULT_ACCESS[f] || "staff";
}

/** 표시용 행 — 그룹 내 기능이 서로 다른 등급이면(예: 잔금만 직원 개방) 개별 행으로 분리. */
export function summaryRows(perm) {
  const out = [];
  for (const row of SUMMARY_ROWS) {
    if (row.features.length <= 1) { out.push(row); continue; }
    const mins = row.features.map((f) => permMin(perm, f));
    if (mins.every((m) => m === mins[0])) { out.push(row); continue; }
    for (const f of row.features) {
      const feat = ACCESS_FEATURES.find((x) => x.key === f);
      out.push({ label: feat ? feat.label : f, features: [f] });
    }
  }
  return out;
}

const STATUS_LABEL = { pending: "승인 대기", approved: "이용 중", blocked: "정지" };
const STATUS_COLOR = { pending: "#b26a00", approved: "#166534", blocked: "#b3261e" };
const INVITE_STATE = {
  valid: { label: "유효", color: "#166534" }, used: { label: "사용됨", color: "#667085" },
  expired: { label: "만료", color: "#b26a00" }, revoked: { label: "취소", color: "#b3261e" },
};

function fmt(at) {
  if (!at) return "";
  try { return new Date(at).toLocaleString("ko-KR", { month: "numeric", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return at.slice(0, 16); }
}

export default function StaffAdmin() {
  const myRole = useRole();
  const canGrantExec = roleAtLeast(myRole, "owner");   // 임원 부여는 사장만
  const { refresh } = useAuth();
  const [perm, setPerm] = useState(null);              // 접근 권한 config(병합됨)
  const [permBusy, setPermBusy] = useState(false);
  const [editPerm, setEditPerm] = useState(false);     // 권한 편집 모드(기본=요약표)
  const [inviteOpen, setInviteOpen] = useState(false); // 초대 링크 만들기 접힘(기본 접힘)
  const [permOpen, setPermOpen] = useState(false);     // 접근 권한 카드 접힘(기본 접힘)
  const [editName, setEditName] = useState("");        // 이름 편집 중인 email
  const [nameDraft, setNameDraft] = useState("");
  const [staff, setStaff] = useState(null);
  const [invites, setInvites] = useState([]);
  const [acts, setActs] = useState([]);
  const [filterEmail, setFilterEmail] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  // 초대 발급 폼
  const [invRole, setInvRole] = useState("staff");
  const [invName, setInvName] = useState("");
  const [newLink, setNewLink] = useState("");
  const [copied, setCopied] = useState(false);

  // 활동 로그에 이메일 대신 이름 — 사장이 한눈에 누군지 알아야 한다(2026-07-26 피드백).
  // 등록 이름이 없으면(사장 본인 등) 이메일 아이디 부분으로 폴백.
  const nameByEmail = useMemo(() => {
    const m = {};
    for (const x of staff || []) if (x.email) m[x.email] = x.name || "";
    return m;
  }, [staff]);
  const staffName = (email) => nameByEmail[email] || String(email || "").split("@")[0] || "-";

  const load = useCallback(async () => {
    setErr("");
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/staff`);
      if (r.status === 403) { setErr("관리자 전용 화면입니다."); setStaff([]); return; }
      if (!r.ok) throw new Error(`목록 실패 (${r.status})`);
      setStaff((await r.json()).items || []);
    } catch (e) { setErr(friendlyErrorMessage(e, "직원 목록을 불러오지 못했습니다.")); setStaff([]); }
  }, []);

  const loadInvites = useCallback(async () => {
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/staff/invites`);
      if (r.ok) setInvites((await r.json()).items || []);
    } catch { /* 표시용 */ }
  }, []);

  const loadActs = useCallback(async () => {
    try {
      const p = filterEmail ? `?email=${encodeURIComponent(filterEmail)}` : "";
      const r = await apiFetch(`${API_BASE_URL}/api/staff/activity${p}`);
      if (r.ok) setActs((await r.json()).items || []);
    } catch { /* 표시용 */ }
  }, [filterEmail]);

  const loadPerm = useCallback(async () => {
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/staff/permissions`);
      setPerm(mergeAccess(r.ok ? (await r.json()).access : null));
    } catch { setPerm(mergeAccess(null)); }
  }, []);

  useEffect(() => { load(); loadInvites(); loadPerm(); }, [load, loadInvites, loadPerm]);
  useEffect(() => { loadActs(); }, [loadActs]);

  const setFeatureRole = async (feature, roleVal) => {
    const next = { ...perm, [feature]: roleVal };
    setPerm(next);
    setPermBusy(true);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/staff/permissions`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ access: next }),
      });
      if (!r.ok) throw new Error(`저장 실패 (${r.status})`);
      refresh?.();   // 앱 전체 메뉴·라우트 가드 즉시 갱신
    } catch (e) { setErr(friendlyErrorMessage(e, "권한 저장에 실패했습니다.")); await loadPerm(); }
    finally { setPermBusy(false); }
  };

  const linkFor = (token) => `${window.location.origin}/join/${token}`;

  const createInvite = async () => {
    setBusy("invite"); setErr(""); setNewLink(""); setCopied(false);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/staff/invite`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role: invRole, name: invName.trim() }),
      });
      if (!r.ok) throw new Error(`초대 발급 실패 (${r.status})`);
      const d = await r.json();
      const link = linkFor(d.token);
      setNewLink(link);
      setInvName("");
      try { await navigator.clipboard.writeText(link); setCopied(true); } catch { /* 수동 복사 */ }
      await loadInvites();
    } catch (e) { setErr(friendlyErrorMessage(e, "초대 발급에 실패했습니다.")); }
    finally { setBusy(""); }
  };

  const copyLink = async (token) => {
    try { await navigator.clipboard.writeText(linkFor(token)); setCopied(true); setNewLink(linkFor(token)); }
    catch { setNewLink(linkFor(token)); }
  };

  const revokeInvite = async (token) => {
    setBusy(token);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/staff/invite/${encodeURIComponent(token)}/revoke`, { method: "POST" });
      if (!r.ok) throw new Error(`취소 실패 (${r.status})`);
      await loadInvites();
    } catch (e) { setErr(friendlyErrorMessage(e, "초대 취소에 실패했습니다.")); }
    finally { setBusy(""); }
  };

  const [confirmKick, setConfirmKick] = useState("");
  // 탈퇴는 상시 노출하지 않는다 — 타이틀 우측 [편집] 토글로만(사장 지시 2026-07-27)
  const [editMode, setEditMode] = useState(false);

  const act = async (email, path, body) => {
    setBusy(email);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/staff/${encodeURIComponent(email)}/${path}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: body ? JSON.stringify(body) : undefined,
      });
      if (!r.ok) throw new Error(`처리 실패 (${r.status})`);
      await load();
    } catch (e) { setErr(friendlyErrorMessage(e, "처리에 실패했습니다.")); }
    finally { setBusy(""); setConfirmKick(""); }
  };

  const saveName = async (email) => {
    setBusy(email);
    try {
      const r = await apiFetch(`${API_BASE_URL}/api/staff/${encodeURIComponent(email)}/name`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: nameDraft.trim() }),
      });
      if (!r.ok) throw new Error(`이름 저장 실패 (${r.status})`);
      setEditName(""); await load();
    } catch (e) { setErr(friendlyErrorMessage(e, "이름 저장에 실패했습니다.")); }
    finally { setBusy(""); }
  };

  // 역할 옵션(부여 상한 내) — 사장만 진현(exec)까지.
  const roleOpts = canGrantExec
    ? [["field", ROLE_LABEL.field], ["staff", ROLE_LABEL.staff], ["manager", ROLE_LABEL.manager], ["exec", ROLE_LABEL.exec]]
    : [["field", ROLE_LABEL.field], ["staff", ROLE_LABEL.staff], ["manager", ROLE_LABEL.manager]];

  const sorted = useMemo(() => {
    const rank = { pending: 0, approved: 1, blocked: 2 };
    return [...(staff || [])].sort((a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3));
  }, [staff]);

  const openInvites = invites.filter((i) => i.state === "valid");

  return (
    <div className="ds-container" style={{ padding: "24px 16px 80px", maxWidth: 900, margin: "0 auto" }}>
      <h2 className="ds-h2" style={{ marginBottom: 4 }}>직원 관리</h2>
      <p className="ds-list-item-meta" style={{ marginBottom: 18 }}>초대 링크 · 승인 · 활동 로그</p>
      {err && <p style={{ color: "#b3261e", fontSize: 13 }}>{err}</p>}

      {/* 초대 링크 발급 */}
      <div className="ds-card" style={{ padding: 16, marginBottom: 16, background: "var(--ds-canvas-card, #fff)", border: "1px solid var(--ds-line, #e6e9ee)", borderRadius: 12 }}>
        <button type="button" onClick={() => setInviteOpen((o) => !o)}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "transparent", border: "none", cursor: "pointer", padding: 0, font: "inherit", textAlign: "left" }}>
          <h3 className="ds-h3" style={{ fontSize: 15, margin: 0 }}>초대 링크 만들기{openInvites.length ? ` · 미사용 ${openInvites.length}건` : ""}</h3>
          <span style={{ color: "var(--ds-ink-soft, #98a0a8)", fontSize: 13, whiteSpace: "nowrap" }}>{inviteOpen ? "접기 ▲" : "펼치기 ▼"}</span>
        </button>
        {inviteOpen && (<div style={{ marginTop: 10 }}>
        <p className="ds-list-item-meta" style={{ marginBottom: 12 }}>
          가입은 이 링크로만 가능합니다. 링크를 직원에게 보내면, 구글/이메일로 가입하는 즉시 아래 권한으로 이용을 시작합니다.
        </p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <select value={invRole} onChange={(e) => setInvRole(e.target.value)}
                  style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--ds-line, #d4d8de)", background: "var(--ds-canvas, #fff)", color: "var(--ds-ink, #1b1e23)", fontSize: 13 }}>
            <option value="field">{ROLE_LABEL.field} (AS·현장 도구만)</option>
            <option value="staff">{ROLE_LABEL.staff} (견적·계약)</option>
            <option value="manager">{ROLE_LABEL.manager} (정산 제외 전부)</option>
            {canGrantExec && <option value="exec">{ROLE_LABEL.exec} (관리자 + SMBM)</option>}
          </select>
          <input type="text" placeholder="이름(선택)" value={invName} onChange={(e) => setInvName(e.target.value)}
                 style={{ padding: "8px 10px", borderRadius: 8, border: "1px solid var(--ds-line, #d4d8de)", background: "var(--ds-canvas, #fff)", color: "var(--ds-ink, #1b1e23)", fontSize: 13, flex: "1 1 140px", minWidth: 120 }} />
          <button type="button" className="ds-btn ds-btn-primary ds-btn-sm" disabled={busy === "invite"} onClick={createInvite}>
            {busy === "invite" ? "생성 중…" : "링크 생성"}
          </button>
        </div>
        {newLink && (
          <div style={{ marginTop: 12, padding: 12, background: "var(--ds-canvas-soft, #f6f8fa)", borderRadius: 8, border: "1px solid var(--ds-line-soft, #eef1f4)" }}>
            <div style={{ fontSize: 12, color: "var(--ds-ink-soft, #667085)", marginBottom: 6 }}>
              {copied ? "✓ 링크가 복사되었습니다 — 직원에게 붙여넣어 보내세요." : "아래 링크를 복사해 직원에게 보내세요."}
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <code style={{ flex: "1 1 240px", fontSize: 12.5, wordBreak: "break-all", color: "var(--ds-ink, #1b1e23)" }}>{newLink}</code>
              <button type="button" className="ds-btn ds-btn-sm" onClick={() => navigator.clipboard?.writeText(newLink).then(() => setCopied(true))}>복사</button>
            </div>
          </div>
        )}
        {openInvites.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "var(--ds-ink-soft, #667085)", marginBottom: 6 }}>미사용 초대 {openInvites.length}건</div>
            {openInvites.map((i) => (
              <div key={i.token} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderTop: "1px solid var(--ds-line-soft, #f0f2f5)", fontSize: 12.5, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 700, color: "#166534" }}>{ROLE_LABEL[i.role] || i.role}</span>
                {i.name && <span style={{ color: "var(--ds-ink, #1b1e23)" }}>{i.name}</span>}
                <span style={{ color: "var(--ds-ink-soft, #98a0a8)" }}>~{fmt(i.expires_at)} 만료</span>
                <span style={{ flex: 1 }} />
                <button type="button" className="ds-btn ds-btn-sm" onClick={() => copyLink(i.token)}>링크 복사</button>
                <button type="button" className="ds-btn ds-btn-sm" disabled={busy === i.token} onClick={() => revokeInvite(i.token)}>취소</button>
              </div>
            ))}
          </div>
        )}
        </div>)}
      </div>

      {/* 직원 목록 (접근 권한보다 위 — 순서 변경 2026-07-24) */}
      <div className="ds-card" style={{ padding: 16, marginBottom: 16, background: "var(--ds-canvas-card, #fff)", border: "1px solid var(--ds-line, #e6e9ee)", borderRadius: 12 }}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
          <h3 className="ds-h3" style={{ fontSize: 15, margin: 0 }}>직원 목록</h3>
          <span style={{ flex: 1 }} />
          <button type="button" className="ds-btn ds-btn-ghost ds-btn-sm"
                  onClick={() => { setEditMode((v) => !v); setConfirmKick(""); }}>
            {editMode ? "완료" : "편집"}
          </button>
        </div>
        {staff === null && <div style={{ color: "var(--ds-ink-soft, #98a0a8)", padding: 20, textAlign: "center" }}>불러오는 중…</div>}
        {staff !== null && sorted.length === 0 && !err && (
          <div style={{ color: "var(--ds-ink-soft, #98a0a8)", padding: 20, textAlign: "center" }}>아직 가입한 직원이 없습니다. 위에서 초대 링크를 만들어 보내세요.</div>
        )}
        {sorted.map((s) => (
          <div key={s.email} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 0", borderTop: "1px solid var(--ds-line-soft, #f0f2f5)", flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 220px", minWidth: 160 }}>
              <div style={{ fontWeight: 600, color: "var(--ds-ink, #1b1e23)", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                {editName === s.email ? (
                  <>
                    <input value={nameDraft} autoFocus placeholder="이름"
                           onChange={(e) => setNameDraft(e.target.value)}
                           onKeyDown={(e) => e.key === "Enter" && saveName(s.email)}
                           style={{ padding: "4px 8px", borderRadius: 6, border: "1px solid var(--ds-line, #d4d8de)", background: "var(--ds-canvas, #fff)", color: "var(--ds-ink, #1b1e23)", fontSize: 13, width: 120 }} />
                    <button type="button" className="ds-btn ds-btn-primary ds-btn-sm" disabled={busy === s.email} onClick={() => saveName(s.email)}>저장</button>
                    <button type="button" className="ds-btn ds-btn-sm" onClick={() => setEditName("")}>취소</button>
                  </>
                ) : (
                  <>
                    {s.name || <span style={{ color: "var(--ds-ink-soft, #98a0a8)" }}>(이름 없음)</span>}
                    <button type="button" title="이름 수정" onClick={() => { setEditName(s.email); setNameDraft(s.name || ""); }}
                            style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--ds-ink-soft, #98a0a8)", fontSize: 12, padding: 2 }}>✎</button>
                    <span style={{ fontWeight: 400, fontSize: 12.5, color: "var(--ds-ink-soft, #98a0a8)" }}>{s.email}</span>
                  </>
                )}
              </div>
              <div style={{ fontSize: 11.5, color: "var(--ds-ink-soft, #98a0a8)" }}>
                {s.requested_at ? `신청 ${fmt(s.requested_at)}` : ""}
                {s.status === "approved" ? ` · ${ROLE_LABEL[s.role] || s.role}` : ""}
              </div>
            </div>
            <span style={{ fontSize: 11.5, fontWeight: 700, padding: "3px 10px", borderRadius: 999, color: "#fff", background: STATUS_COLOR[s.status] || "#667085" }}>
              {STATUS_LABEL[s.status] || s.status}
            </span>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {s.status === "pending" && (
                <>
                  <button type="button" className="ds-btn ds-btn-primary ds-btn-sm" disabled={busy === s.email}
                          onClick={() => act(s.email, "approve", { role: "staff" })}>직원 승인</button>
                  <button type="button" className="ds-btn ds-btn-sm" disabled={busy === s.email}
                          onClick={() => act(s.email, "approve", { role: "manager" })}>관리자 승인</button>
                  {canGrantExec && (
                    <button type="button" className="ds-btn ds-btn-sm" disabled={busy === s.email}
                            onClick={() => act(s.email, "approve", { role: "exec" })}>{ROLE_LABEL.exec} 승인</button>
                  )}
                </>
              )}
              {s.status !== "pending" && s.role !== "owner" && confirmKick !== s.email && (
                <>
                  {/* 역할 변경 */}
                  <select value={s.role} disabled={busy === s.email}
                          onChange={(e) => act(s.email, "role", { role: e.target.value })}
                          style={{ padding: "5px 8px", borderRadius: 8, border: "1px solid var(--ds-line, #d4d8de)", background: "var(--ds-canvas, #fff)", color: "var(--ds-ink, #1b1e23)", fontSize: 12.5 }}>
                    {roleOpts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  {s.status === "blocked" ? (
                    <button type="button" className="ds-btn ds-btn-sm" disabled={busy === s.email}
                            onClick={() => act(s.email, "block", { active: true })}>해제</button>
                  ) : (
                    <button type="button" className="ds-btn ds-btn-sm" disabled={busy === s.email}
                            onClick={() => act(s.email, "block", { active: false })}>정지</button>
                  )}
                  {editMode && (
                    <button type="button" className="ds-btn ds-btn-sm" disabled={busy === s.email}
                            style={{ color: "#b3261e" }} onClick={() => setConfirmKick(s.email)}>탈퇴</button>
                  )}
                </>
              )}
              {confirmKick === s.email && (
                <>
                  <span style={{ fontSize: 12.5, color: "#b3261e" }}>강제 탈퇴할까요?</span>
                  <button type="button" className="ds-btn ds-btn-sm" disabled={busy === s.email}
                          style={{ background: "#b3261e", color: "#fff", borderColor: "#b3261e" }}
                          onClick={() => act(s.email, "remove")}>탈퇴 확인</button>
                  <button type="button" className="ds-btn ds-btn-sm" onClick={() => setConfirmKick("")}>취소</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* 접근 권한 — 요약표 기본, '권한 편집'으로 설정 활성화 */}
      <div className="ds-card" style={{ padding: 16, marginBottom: 16, background: "var(--ds-canvas-card, #fff)", border: "1px solid var(--ds-line, #e6e9ee)", borderRadius: 12 }}>
        <button type="button" onClick={() => setPermOpen((o) => !o)}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, background: "transparent", border: "none", cursor: "pointer", padding: 0, font: "inherit", textAlign: "left" }}>
          <h3 className="ds-h3" style={{ fontSize: 15, margin: 0 }}>접근 권한</h3>
          <span style={{ color: "var(--ds-ink-soft, #98a0a8)", fontSize: 13, whiteSpace: "nowrap" }}>{permOpen ? "접기 ▲" : "펼치기 ▼"}</span>
        </button>
        {permOpen && (<div style={{ marginTop: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <span style={{ flex: 1 }} />
          <button type="button" className="ds-btn ds-btn-sm" onClick={() => setEditPerm((v) => !v)}>
            {editPerm ? "요약 보기" : "권한 편집"}
          </button>
        </div>
        {perm === null && <div style={{ color: "var(--ds-ink-soft, #98a0a8)", padding: 12, textAlign: "center" }}>불러오는 중…</div>}

        {perm && !editPerm && (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "8px 6px" }} />
                  {PERM_COLS.map(([, l]) => (
                    <th key={l} style={{ padding: "8px 6px", color: "var(--ds-ink-soft, #667085)", fontWeight: 600, textAlign: "center", minWidth: 52 }}>{l}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {summaryRows(perm).map((row) => {
                  const min = rowMin(perm, row.features);
                  return (
                    <tr key={row.label} style={{ borderTop: "1px solid var(--ds-line-soft, #f0f2f5)" }}>
                      <td style={{ padding: "9px 6px", color: "var(--ds-ink, #1b1e23)" }}>{row.label}</td>
                      {PERM_COLS.map(([r]) => {
                        // 등급 임계 + **역할 제외**(#56 — 진현은 계약 계열 제외)
                        const ok = roleAtLeast(r, min)
                          && !row.features.some((f) => deniedFor(DEFAULT_DENY, f).includes(r));
                        return (
                          <td key={r} style={{ padding: "9px 6px", textAlign: "center" }}>{ok ? "✅" : "❌"}</td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {perm && editPerm && (
          <div>
            <p className="ds-list-item-meta" style={{ marginBottom: 8 }}>
              각 기능을 <b>어느 등급부터</b> 볼지 정합니다. 바꾸면 즉시 반영됩니다. (계약서·전자계약은 <b>진현 제외</b> — 외부 하도급사, 사장 결정 2026-07-31) (테마만 전원 공통 — 견적 계열은 '직원'부터가 기본)
            </p>
            {ACCESS_FEATURES.map((f) => (
              <div key={f.key} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", borderTop: "1px solid var(--ds-line-soft, #f0f2f5)", flexWrap: "wrap" }}>
                <span style={{ flex: 1, minWidth: 140, fontSize: 13.5, color: "var(--ds-ink, #1b1e23)" }}>{f.label}</span>
                <select value={perm[f.key]} disabled={permBusy}
                        onChange={(e) => setFeatureRole(f.key, e.target.value)}
                        style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid var(--ds-line, #d4d8de)", background: "var(--ds-canvas, #fff)", color: "var(--ds-ink, #1b1e23)", fontSize: 13 }}>
                  {PERM_ROLE_OPTS.map(([v, l]) => <option key={v} value={v}>{l} 이상</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
        </div>)}
      </div>

      {/* 활동 로그 */}
      <div className="ds-card" style={{ padding: 16, background: "var(--ds-canvas-card, #fff)", border: "1px solid var(--ds-line, #e6e9ee)", borderRadius: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
          <h3 className="ds-h3" style={{ fontSize: 15, margin: 0 }}>활동 로그</h3>
          <span style={{ flex: 1 }} />
          <select value={filterEmail} onChange={(e) => setFilterEmail(e.target.value)}
                  style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid var(--ds-line, #d4d8de)", background: "var(--ds-canvas, #fff)", color: "var(--ds-ink, #1b1e23)", fontSize: 13 }}>
            <option value="">전체 직원</option>
            {(staff || []).map((s) => <option key={s.email} value={s.email}>{s.name || s.email}</option>)}
          </select>
        </div>
        {acts.length === 0 && <div style={{ color: "var(--ds-ink-soft, #98a0a8)", padding: 16, textAlign: "center", fontSize: 13 }}>기록이 없습니다.</div>}
        {groupActivities(acts).map((g, i) => (
          <div key={i} title={`${g.method} ${g.path}`}
               style={{ display: "flex", gap: 10, alignItems: "center", padding: "7px 0", borderTop: "1px solid var(--ds-line-soft, #f0f2f5)", fontSize: 12.5, color: "var(--ds-ink, #1b1e23)" }}>
            <span style={{ whiteSpace: "nowrap", color: "var(--ds-ink-soft, #98a0a8)", flex: "0 0 88px" }}>{fmt(g.at)}</span>
            <span style={{ flex: "0 0 96px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 600 }}>{staffName(g.email)}</span>
            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {g.target && <b style={{ fontWeight: 600 }}>{g.target}</b>}
              {g.target ? " " : ""}{g.action}
            </span>
            {g.count > 1 && (
              <span style={{ flex: "0 0 auto", fontSize: 11.5, fontWeight: 600, padding: "1px 7px", borderRadius: 999, background: "var(--ds-line-soft, #f0f2f5)", color: "var(--ds-ink-soft, #667085)", whiteSpace: "nowrap" }}>
                {g.count}회
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
